import { useEffect, useRef } from 'react';

/**
 * Calls `onTick` every `intervalMs` while `enabled` and the tab is visible. Coming back to the
 * tab ticks right away; `immediate` also ticks as soon as it gets enabled.
 */
export function useVisibleInterval(
    onTick: () => void,
    params: { enabled: boolean; intervalMs: number; immediate: boolean },
): void {
    const { enabled, intervalMs, immediate } = params;
    const onTickRef = useRef(onTick);
    useEffect(() => {
        onTickRef.current = onTick;
    });

    useEffect(() => {
        if (!enabled) return;
        let interval: number | null = null;
        const start = () => {
            if (interval === null) interval = window.setInterval(() => onTickRef.current(), intervalMs);
        };
        const stop = () => {
            if (interval === null) return;
            window.clearInterval(interval);
            interval = null;
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                onTickRef.current();
                start();
            } else {
                stop();
            }
        };

        if (document.visibilityState === 'visible') {
            if (immediate) onTickRef.current();
            start();
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            stop();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, intervalMs, immediate]);
}
